/**
 * Environment Configuration & Security Boundaries
 *
 * Meme Mandi - Phase 1 Foundation
 *
 * CRITICAL SECURITY INVARIANTS:
 * 1. Only variables with `VITE_` prefix are bundled into client code.
 * 2. Service-role secrets, admin tokens, or database passwords must NEVER be placed here.
 * 3. Only the Supabase Public Anonymous Key (`VITE_SUPABASE_ANON_KEY`) is allowed in the browser.
 */

export interface AppEnvConfig {
  appEnv: 'development' | 'test' | 'production';
  supabaseUrl: string;
  supabaseAnonKey: string;
  isSupabaseConfigured: boolean;
  isDevelopment: boolean;
  isProduction: boolean;
  isTest: boolean;
}

/**
 * Validates a URL string format safely without throwing unhandled exceptions.
 */
function isValidUrl(urlString?: string): boolean {
  if (!urlString || typeof urlString !== 'string') return false;
  try {
    const parsed = new URL(urlString);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Resolves and validates environment configuration safely.
 */
export function getEnvConfig(): AppEnvConfig {
  const rawEnv: Partial<ImportMetaEnv> & Record<string, string | undefined> =
    typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : {};

  const appEnvRaw = (rawEnv.VITE_APP_ENV || rawEnv.MODE || 'development').toLowerCase();
  const appEnv: 'development' | 'test' | 'production' =
    appEnvRaw === 'production'
      ? 'production'
      : appEnvRaw === 'test'
        ? 'test'
        : 'development';

  const supabaseUrl = (rawEnv.VITE_SUPABASE_URL || '').trim();
  const supabaseAnonKey = (rawEnv.VITE_SUPABASE_ANON_KEY || '').trim();

  // Basic check to see if Supabase credentials are configured and not placeholder values
  const hasValidUrl =
    isValidUrl(supabaseUrl) && !supabaseUrl.includes('placeholder-project');
  const hasValidKey =
    supabaseAnonKey.length > 20 &&
    !supabaseAnonKey.includes('placeholder') &&
    !supabaseAnonKey.includes('replace-with-real-key');

  const isSupabaseConfigured = hasValidUrl && hasValidKey;

  return {
    appEnv,
    supabaseUrl,
    supabaseAnonKey,
    isSupabaseConfigured,
    isDevelopment: appEnv === 'development',
    isProduction: appEnv === 'production',
    isTest: appEnv === 'test',
  };
}

export const env = getEnvConfig();
